import pin from '@assets/images/google-map/pin.webp';
import {
  GOOGLE_MAPS_API_KEY,
  googleMapThemeOptions,
} from '@features/r8tr/map/google-map/googleMapTheme';
import { useAppSelector } from '@lib/store/hooks';
import Box from '@mui/material/Box';
import GoogleMapReact, {
  type GoogleCircle,
  type GoogleMapHandle,
  type GoogleMapsNamespace,
} from 'google-map-react';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

const themeRgb = (name: string): string => {
  const channels = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return `rgb(${channels})`;
};

const markerStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: '50%',
  height: 30,
  transform: 'translate(-50%, -100%)',
};

const METERS_PER_MILE = 1609.344;
const DEFAULT_RADIUS_MILES = 10;
const INITIAL_ZOOM = 10;
const FIT_PADDING_PX = 16;
const MAP_HEIGHT_PX = 220;
const FADE_IN_MS = 180;
const CIRCLE_STYLE = {
  strokeOpacity: 0.7,
  strokeWeight: 2,
  fillOpacity: 0.12,
} as const;

const milesToMeters = (miles: number): number => miles * METERS_PER_MILE;

const AreaPin = ({ lat, lng }: { lat: number; lng: number }): JSX.Element => (
  <img src={pin} alt="" data-coord={`${lat},${lng}`} style={markerStyle} />
);

interface ServiceAreaMapProps {
  latitude: number;
  longitude: number;
  radiusMiles: number;
}

export const ServiceAreaMap = ({
  latitude,
  longitude,
  radiusMiles,
}: ServiceAreaMapProps): JSX.Element => {
  const mode = useAppSelector((state) => state.colorMode.mode);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<GoogleMapHandle | null>(null);
  const mapsRef = useRef<GoogleMapsNamespace | null>(null);
  const circleRef = useRef<GoogleCircle | null>(null);
  const [shownKey, setShownKey] = useState<string | null>(null);
  const miles = Number.isFinite(radiusMiles)
    ? radiusMiles
    : DEFAULT_RADIUS_MILES;
  const center = { lat: latitude, lng: longitude };
  const mapKey = `${latitude}:${longitude}`;
  const centerRef = useRef(center);
  const milesRef = useRef(miles);
  centerRef.current = center;
  milesRef.current = miles;

  const syncView = useCallback((): void => {
    const map = mapRef.current;
    const maps = mapsRef.current;
    const circle = circleRef.current;
    if (!map || !maps || !circle) {
      return;
    }
    maps.event.trigger(map, 'resize');
    circle.setCenter(centerRef.current);
    circle.setRadius(milesToMeters(milesRef.current));
    const bounds = circle.getBounds();
    if (bounds) {
      map.fitBounds(bounds, FIT_PADDING_PX);
    } else {
      map.setCenter(centerRef.current);
    }
  }, []);

  useEffect(() => {
    syncView();
  }, [latitude, longitude, miles, syncView]);

  useEffect(() => {
    const node = boxRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        syncView();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [syncView]);

  return (
    <Box
      ref={boxRef}
      sx={{
        mt: 1.75,
        overflow: 'hidden',
        height: MAP_HEIGHT_PX,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        opacity: shownKey === mapKey ? 1 : 0,
        transition: `opacity ${FADE_IN_MS}ms ease`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <GoogleMapReact
        key={`${mapKey}:${mode}`}
        bootstrapURLKeys={{ key: GOOGLE_MAPS_API_KEY }}
        defaultCenter={center}
        defaultZoom={INITIAL_ZOOM}
        options={(maps) => ({
          ...googleMapThemeOptions(mode, maps),
          isFractionalZoomEnabled: true,
        })}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          mapRef.current = map;
          mapsRef.current = maps;
          circleRef.current = new maps.Circle({
            ...CIRCLE_STYLE,
            strokeColor: themeRgb('--primary'),
            fillColor: themeRgb('--primary'),
            map,
          });
          syncView();
          maps.event.addListenerOnce(map, 'idle', () => {
            syncView();
            setShownKey(mapKey);
          });
        }}
      >
        <AreaPin lat={latitude} lng={longitude} />
      </GoogleMapReact>
    </Box>
  );
};
